import React, { useState, useRef } from 'react';
import { Grid, Button, TextField, FormControlLabel, Checkbox } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { eventsService } from '../../../../core/services/eventsService';
import styles from '../SchedulerSetting.module.scss';

interface UploadBanquetEventOrderProps {
	existingDocumentsCount: number;
	existingNames: string[];
	handleBackClick?: () => void;
	onDocumentUploaded?: () => void;
}

const MAX_MB = 10;
const MAX_BYTES = MAX_MB * 1024 * 1024;

function makeId(): string {
	return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function uniqueNameFromFilename(base: string, used: Set<string>): string {
	const name = base.replace(/\.pdf$/i, '');
	let candidate = name;
	let n = 2;
	while (used.has(candidate.toLowerCase())) {
		candidate = `${name} (${n})`;
		n += 1;
	}
	used.add(candidate.toLowerCase());
	return candidate;
}

const UploadBanquetEventOrder: React.FC<UploadBanquetEventOrderProps> = ({
	existingDocumentsCount,
	existingNames,
	handleBackClick,
	onDocumentUploaded,
}) => {
	const [name, setName] = useState('');
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [active, setActive] = useState(true);
	const [isUploading, setIsUploading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			if (file.type !== 'application/pdf') {
				setError('Only PDF files are allowed');
				setSelectedFile(null);
				return;
			}

			const maxSize = 10 * 1024 * 1024;
			if (file.size > maxSize) {
				setError('File size must be less than 10MB');
				setSelectedFile(null);
				return;
			}

			const trimmedName = name.trim();
			if (trimmedName && existingNames.some((n) => n.toLowerCase() === trimmedName.toLowerCase())) {
				setError(`A document named "${trimmedName}" already exists. Names must be unique.`);
				setSelectedFile(null);
				return;
			}

			setSelectedFile(file);
			setError(null);

			if (!name.trim()) {
				const nameWithoutExt = file.name.replace(/\.pdf$/i, '');
				setName(nameWithoutExt);
			}
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		const trimmed = name.trim();
		if (!trimmed) {
			setError('Name is required');
			return;
		}

		if (existingNames.some((n) => n.toLowerCase() === trimmed.toLowerCase())) {
			setError(`A document named "${trimmed}" already exists. Names must be unique.`);
			return;
		}

		if (!selectedFile) {
			setError('Please select a PDF file to upload');
			return;
		}

		if (existingDocumentsCount >= 50) {
			setError('Maximum of 50 documents reached');
			return;
		}

		setIsUploading(true);

		try {
			const nextSortOrder = existingDocumentsCount + 1;
			await eventsService.uploadEventDocumentTemplate(selectedFile, trimmed, nextSortOrder, undefined, active);
			onDocumentUploaded?.();
			setTimeout(() => {
				handleBackClick?.();
			}, 500);
		} catch (err: any) {
			setError(err?.message || 'Failed to upload document');
		} finally {
			setIsUploading(false);
		}
	};

	return (
		<div className={styles.editGeneralSettings}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Button className={styles.backButton} onClick={handleBackClick} variant="contained" disabled={isUploading}>
						Back
					</Button>
					<Button
						className={styles.submitButton}
						onClick={handleSubmit}
						disabled={isUploading || !selectedFile}
						variant="contained"
					>
						{isUploading ? 'Uploading...' : 'Upload'}
					</Button>
				</Grid>
				<Grid>
					<h1 className={styles.formTitle}>Upload Banquet Event Order</h1>
				</Grid>
			</Grid>

			<form className={styles.formContainer} onSubmit={handleSubmit}>
				{error && <div style={{ color: '#d32f2f', marginBottom: 16 }}>{error}</div>}

				<div
					style={{
						border: selectedFile ? '2px dashed #4caf50' : '2px dashed #ccc',
						borderRadius: 8,
						padding: 24,
						textAlign: 'center',
						marginBottom: 16,
						cursor: 'pointer',
						backgroundColor: '#fafafa',
					}}
					onClick={() => fileInputRef.current?.click()}
				>
					<input
						type="file"
						ref={fileInputRef}
						accept=".pdf,application/pdf"
						style={{ display: 'none' }}
						onChange={handleFileSelect}
					/>
					<CloudUpload style={{ fontSize: 48, color: '#999', marginBottom: 8 }} />
					<div style={{ marginBottom: 4 }}>
						{selectedFile ? selectedFile.name : 'Click to select a PDF file'}
					</div>
					<div style={{ fontSize: '0.75rem', color: '#999' }}>
						Maximum file size: 10MB | Format: PDF only
					</div>
				</div>
				<TextField
					label="Name"
					name="name"
					value={name}
					onChange={(e) => {
						setName(e.target.value);
						setError(null);
					}}
					className={styles.input}
					margin="normal"
					fullWidth
					required
					helperText="Label shown for this banquet event order document"
					inputProps={{
						maxLength: 100,
					}}
				/>

				<FormControlLabel
					control={
						<Checkbox
							checked={active}
							onChange={(_, checked) => {
								setActive(checked);
								setError(null);
							}}
							color="primary"
						/>
					}
					label="Active"
					className={styles.input}
					sx={{ mt: 1, display: 'block' }}
				/>
			</form>
		</div>
	);
};

export default UploadBanquetEventOrder;
